/* eslint-disable */
import { ChangeEvent, useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import * as yup from "yup";

import SelectInput from "../../components/common/SelectInput";
import DatePicker from "../../components/common/DatePicker";
import { SubmitClaimRequest } from "../../models/request/submitClaimRequest";
import {
  useGetPoliciesAvailableForClaimCreationQuery,
  useSubmitClaimMutation,
} from "../../store/claimSlice";
import { InfoCircle, Insurance, ShieldCheck } from "../../common/icons";
import { PolicyBasicInfo } from "../../models/policyBasicInfo";
import { SplashMessage } from "../../common/SplashMessage";
import { LocalizationContext } from "../../App";
import { Formatters } from "../../common/helpers/formatters";
import { useGetPoliciesQuery } from "../../store/policiesSlice";
import { RootState, useAppSelector } from "../../store/store";

/*
 * Returns 1 if the IBAN is valid
 * Returns FALSE if the IBAN's length is not as should be (for CY the IBAN Should be 28 chars long starting with CY )
 * Returns any other number (checksum) when the IBAN is invalid (check digits do not match)
 */
function isValidIBANNumber(input: string) {
  var CODE_LENGTHS: any = {
    AD: 24,
    AE: 23,
    AT: 20,
    AZ: 28,
    BA: 20,
    BE: 16,
    BG: 22,
    BH: 22,
    BR: 29,
    CH: 21,
    CR: 21,
    CY: 28,
    CZ: 24,
    DE: 22,
    DK: 18,
    DO: 28,
    EE: 20,
    ES: 24,
    FI: 18,
    FO: 18,
    FR: 27,
    GB: 22,
    GI: 23,
    GL: 18,
    GR: 27,
    GT: 28,
    HR: 21,
    HU: 28,
    IE: 22,
    IL: 23,
    IS: 26,
    IT: 27,
    JO: 30,
    KW: 30,
    KZ: 20,
    LB: 28,
    LI: 21,
    LT: 20,
    LU: 20,
    LV: 21,
    MC: 27,
    MD: 24,
    ME: 22,
    MK: 19,
    MR: 27,
    MT: 31,
    MU: 30,
    NL: 18,
    NO: 15,
    PK: 24,
    PL: 28,
    PS: 29,
    PT: 25,
    QA: 29,
    RO: 24,
    RS: 22,
    SA: 24,
    SE: 24,
    SI: 19,
    SK: 24,
    SM: 27,
    TN: 24,
    TR: 26,
    AL: 28,
    BY: 28,
    EG: 29,
    GE: 22,
    IQ: 23,
    LC: 32,
    SC: 31,
    ST: 25,
    SV: 28,
    TL: 23,
    UA: 29,
    VA: 22,
    VG: 24,
    XK: 20,
  };
  var iban = String(input)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, ""), // keep only alphanumeric characters
    code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/), // match and capture (1) the country code, (2) the check digits, and (3) the rest
    digits;
  // check syntax and length
  if (!code || iban.length !== CODE_LENGTHS[code[1]]) {
    return false;
  }
  // rearrange country code and check digits, and convert chars to ints
  digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
    return (letter.charCodeAt(0) - 55).toString();
  });
  // final check
  return mod97(digits) === 1;
}

function mod97(str: string) {
  var checksum: any = str.slice(0, 2),
    fragment;
  for (var offset = 2; offset < str.length; offset += 7) {
    fragment = String(checksum) + str.substring(offset, offset + 7);
    checksum = parseInt(fragment, 10) % 97;
  }
  return checksum;
}

const claimDetailsSchema = yup.object().shape({
  incidentDate: yup.date().required("Erforderlich"),
  reasonForClaimReport: yup.number().required("Erforderlich"),
});

const bankDetailsSchema = yup.object().shape({
  nameOfAccountHolder: yup.string().required("Erforderlich"),
  nameOfBank: yup.string().required("Erforderlich"),
  bankZIPCode: yup.string().required("Erforderlich"),
  IBANnumber: yup
    .string()
    .required("Erforderlich")
    .test("ibanValidator", "IBAN ungültig", function (value) {
      const { path, createError } = this;

      if (isValidIBANNumber(value)) return true;
      else return createError({ path, message: "IBAN ungültig" });
    }),
});

const summarySchema = yup.object().shape({
  aboveIsTruthful: yup.boolean().isTrue(),
});

type ClaimSubmitFormValues = {
  incidentDate: Date;
  reasonForClaimReport: number;
  nameOfAccountHolder: string;
  nameOfBank: string;
  bankZIPCode: string;
  IBANnumber: string;
  truthDeclaration: boolean;
};

type StepHeader = {
  id: number;
  name: string;
  next?: string;
};

enum SubmitClaimStep {
  ClaimDetails = 1,
  BankDetails,
  Summary,
}

const SubmitClaim = () => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState<ClaimSubmitFormValues>();
  const [step, setStep] = useState<SubmitClaimStep>(SubmitClaimStep.ClaimDetails);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyBasicInfo>();
  const [errors, setErrors] = useState<any>();
  const l10n = useContext(LocalizationContext);
  const [isFormValidated, setIsFormValidated] = useState<boolean>(false);
  const stepHeaders: StepHeader[] = [
    {
      id: 1,
      name: l10n.title.claimDetails,
      next: l10n.title.bankDetails,
    },
    {
      id: 2,
      name: l10n.title.bankDetails,
      next: l10n.title.summary,
    },
    {
      id: 3,
      name: l10n.title.summary,
    },
  ];
  const reasonForClaimReportOptions: {
    value: number;
    label: string;
    descriptionForDatepickerField: string;
  }[] = [
    {
      value: 1,
      label: l10n.label.unemployment,
      descriptionForDatepickerField: l10n.notice.whenDoesYourEmploymentEnd,
    },
    {
      value: 2,
      label: l10n.label.incapacityForWorkIllness,
      descriptionForDatepickerField: l10n.notice.sinceWhenAreYouDisabled,
    },
    {
      value: 3,
      label: l10n.label.incapacityForWorkAccident,
      descriptionForDatepickerField: l10n.notice.sinceWhenAreYouDisabled,
    },
  ];
  const [finalApproval, setFinalApproval] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [minClaimDate, setMinClaimDate] = useState<Date | undefined>(undefined);
  const [maxClaimDate, setMaxClaimDate] = useState<Date | undefined>(new Date());
  const { data: policiesBasic, isLoading: arePoliciesLoading } =
    useGetPoliciesAvailableForClaimCreationQuery();
  const [submitClaim, { isLoading: isSubmitting, isSuccess }] = useSubmitClaimMutation();
  const { data: policies, isLoading: isLoading } = useGetPoliciesQuery();
  const language = useAppSelector((state: RootState) => state.app.appLanguage);

  useEffect(() => {
    if (policiesBasic?.length) {
      let paymentProtectionPolicy = policiesBasic.find((p) => p.policyNumber.endsWith("PPI"));
      setSelectedPolicy(paymentProtectionPolicy);
    }
  }, [policiesBasic]);

  useEffect(() => {
    if (policies && selectedPolicy) {
      const policyValidFromDate = policies.find(
        (p) => p.uniqueIdentifier === selectedPolicy.uniqueIdentifier
      )?.validFromDate;
      setMinClaimDate(policyValidFromDate);
    }
  }, [policies, selectedPolicy]);

  useEffect(() => {
    if (formValues && isFormValidated) {
      validate();
    }
  }, [formValues]);

  useEffect(() => {
    if (formValues?.reasonForClaimReport) {
      // Unemployment
      if (formValues?.reasonForClaimReport === 1) {
        setMaxClaimDate(undefined);
      } else {
        setMaxClaimDate(new Date());
      }
    }
  }, [formValues?.reasonForClaimReport]);

  useEffect(() => {
    if (formValues?.incidentDate && maxClaimDate && formValues.incidentDate.getTime() > maxClaimDate.getTime()) {
      setFormValues({
        ...formValues,
        incidentDate: maxClaimDate
      });
    }
  }, [formValues?.incidentDate, maxClaimDate]);

  const validate = (): boolean => {
    try {
      switch (step) {
        case 1:
          claimDetailsSchema.validateSync(formValues, { abortEarly: false });
          break;
        case 2:
          bankDetailsSchema.validateSync(formValues, { abortEarly: false });
          break;
        default:
          summarySchema.validateSync(formValues, { abortEarly: false });
          break;
      }
      setIsFormValidated(false);
      setErrors(null);
      return true;
    } catch (error: any) {
      let errorMessages: any = {};
      for (let innerError of error.inner) {
        errorMessages[innerError.path] = innerError.message;
      }
      setErrors(errorMessages);
      setIsFormValidated(true);
      return false;
    }
  };

  const handleSelectedInputChange = (updatedData: any) => {
    let updatedFormValues = { ...formValues } as ClaimSubmitFormValues;
    setFormValues({
      ...updatedFormValues,
      reasonForClaimReport: updatedData.value,
    });
  };

  const handleDateInputChange = (updatedData: Date | null) => {
    if (updatedData) {
      const utcDate = new Date(Date.UTC(
        updatedData.getFullYear(),
        updatedData.getMonth(),
        updatedData.getDate(),
        updatedData.getHours(),
        updatedData.getMinutes(),
        updatedData.getSeconds()
      ));

      let updatedFormValues = {
        ...formValues,
        incidentDate: utcDate,
      } as ClaimSubmitFormValues;

      setFormValues(updatedFormValues);
    }
  };

  const handleChange = (event: ChangeEvent<any>) => {
    const { name, value } = event.target;
    let updatedFormValues = {
      ...formValues,
      [name]: value,
    } as ClaimSubmitFormValues;
    setFormValues(updatedFormValues);
  };

  const handleNext = () => {
    if (validate()) {
      setStep(step + 1);
      setIsFormValidated(false);
    } else {
      setIsFormValidated(true);
    }
  };

  const handleSubmit = () => {
    if (!selectedPolicy) return;

    if (validate()) {
      const submitClaimRequest: SubmitClaimRequest = {
        claimDefinitionVersionCode: selectedPolicy.claimDefinitionVersionCode,
        policyUniqueCode: selectedPolicy.uniqueIdentifier,
        language: language ?? "de",
        claimFNOLData: {
          PPIClaimSubmission: [],
          ...(formValues ?? {}),
        },
      };

      if (submitClaimRequest.claimFNOLData["truthDeclaration"]) {
        submitClaimRequest.claimFNOLData["truthDeclaration"] = 1;
      } else {
        submitClaimRequest.claimFNOLData["truthDeclaration"] = 2;
      }

      submitClaim(submitClaimRequest);
    }
  };

  const currentStepHeader = stepHeaders.find((header) => header.id === step);

  if (arePoliciesLoading)
    return (
      <div className="loading-screen flex flex-col justify-center items-center">
        <div className="text-body text-xl">{l10n.common.loading}</div>
      </div>
    );

  if (!isLoading && !policies?.length) navigate("/");

  if (isSuccess) {
    setTimeout(() => {
      navigate("/");
    }, 3000);

    return (
      <SplashMessage backgroundTransparency={0}>
        <div className="flex flex-col justify-center items-center gap-6">
          <ShieldCheck className={"w-32 h-32 stroke-splash stroke-2"} />
          <h1 className="splash-message-title">{l10n.notice.success}</h1>
          <div
            className="text-center text-lg"
            dangerouslySetInnerHTML={{
              __html: l10n.notice.youHaveSuccessfullySubmittedYourClaimAxaWillContactYou,
            }}
          />
        </div>
      </SplashMessage>
    );
  }

  return (
    <div className="grid gap-8">
      <div className="flex">
        <div className="flex items-center leading-none">
          <Insurance className="w-10 h-10 stroke-1.5 stroke-body" />
          <span className="mt-[2px] ml-2 text-base font-bold tracking-wide">
            {l10n.products.PPI.name}
          </span>
        </div>
      </div>

      <div className="w-full flex justify-center items-center">
        <div className="flex flex-col items-center">
          <span className="text-2xl text-center font-semibold -mt-1 mb-1 last:mb-0">
            {step}. {currentStepHeader?.name}
          </span>
          {currentStepHeader?.next && (
            <span className="text-body text-sm font-normal leading-none">
              {l10n.common.next}: {currentStepHeader.next}
            </span>
          )}
        </div>
      </div>

      <>
        <form autoComplete="off" className={`${isFormValidated ? "validated" : ""}`}>
          {(() => {
            switch (step) {
              case SubmitClaimStep.ClaimDetails:
                return (
                  <>
                    <div className="form-group">
                      <label htmlFor="reasonForClaimReport">{l10n.label.insuranceEvent}</label>
                      <SelectInput
                        id="reasonForClaimReport"
                        onChange={handleSelectedInputChange}
                        options={reasonForClaimReportOptions}
                        placeholder="Reason for claim report"
                        preSelectedOption={null}
                        required
                        tabIndex={0}
                      />

                      {errors?.reasonForClaimReport && (
                        <span className="invalid-feedback">{errors?.reasonForClaimReport}</span>
                      )}
                    </div>

                    {formValues?.reasonForClaimReport &&
                      reasonForClaimReportOptions.findIndex(
                        (opt) => formValues.reasonForClaimReport === opt.value
                      ) > -1 && (
                        <div className="my-5 notification notification-info">
                          <InfoCircle className="w-[24px] h-[24px] shrink-0 fill-info" />
                          {
                            reasonForClaimReportOptions.find(
                              (r) => formValues.reasonForClaimReport === r.value
                            )?.descriptionForDatepickerField
                          }
                        </div>
                      )}

                    <div className="form-group">
                      <label htmlFor="incidentDate">{l10n.label.dateOfEvent}</label>
                      <DatePicker
                        className="form-control"
                        excludeDates={[]}
                        id="incidentDate"
                        maxDate={maxClaimDate}
                        minDate={minClaimDate}
                        onChange={handleDateInputChange}
                        placeholder={"DD.MM.YYYY"}
                        required
                        showIcon={true}
                        value={formValues?.incidentDate}
                        wrapperClassName="w-full"
                      />
                      {errors?.incidentDate && (
                        <span className="invalid-feedback">{errors?.incidentDate}</span>
                      )}
                    </div>
                  </>
                );
              case SubmitClaimStep.BankDetails:
                return (
                  <>
                    <div className="form-group">
                      <label htmlFor="nameOfAccountHolder">{l10n.label.accountHolderName}</label>
                      <input
                        autoComplete="off"
                        autoFocus
                        className="form-control"
                        id="nameOfAccountHolder"
                        name="nameOfAccountHolder"
                        onChange={handleChange}
                        placeholder="e.g. John Doe"
                        required
                        tabIndex={0}
                        title="Name of account holder"
                      />
                      {errors?.nameOfAccountHolder && (
                        <span className="invalid-feedback">{errors?.nameOfAccountHolder}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="nameOfBank">{l10n.label.bankName}</label>
                      <input
                        autoComplete="off"
                        className="form-control"
                        id="nameOfBank"
                        name="nameOfBank"
                        onChange={handleChange}
                        placeholder="e.g. UBS"
                        required
                        title="Name of bank"
                      />
                      {errors?.nameOfBank && (
                        <span className="invalid-feedback">{errors?.nameOfBank}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="bankZIPCode">{l10n.label.bankPostalCode}</label>
                      <input
                        autoComplete="off"
                        className="form-control"
                        id="bankZIPCode"
                        name="bankZIPCode"
                        onChange={handleChange}
                        placeholder="e.g. 8050"
                        required
                        title="Bank ZIP code"
                      />
                      {errors?.bankZIPCode && (
                        <span className="invalid-feedback">{errors?.bankZIPCode}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="IBANnumber">{l10n.label.iban}</label>
                      <input
                        autoComplete="off"
                        className="form-control"
                        id="IBANnumber"
                        name="IBANnumber"
                        onChange={handleChange}
                        placeholder="e.g. CH1239144886522738123"
                        required
                        title="IBAN number"
                      />
                      {errors?.IBANnumber && (
                        <span className="invalid-feedback">{errors?.IBANnumber}</span>
                      )}
                    </div>
                  </>
                );
              case SubmitClaimStep.Summary:
                return (
                  formValues && (
                    <>
                      <div className="card">
                        <div className="card-body">
                          <div className="grid grid-cols-1 gap-2">
                            <div className="flex flex-col">
                              <div className="text-sm">{l10n.label.insuranceEvent}</div>
                              <div className="flex font-semibold">
                                {
                                  reasonForClaimReportOptions.find(
                                    (r) => r.value === formValues.reasonForClaimReport
                                  )?.label
                                }
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm">{l10n.label.dateOfEvent}</span>
                              <span className="flex font-semibold">
                                {Formatters.formatShortDate(formValues.incidentDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="card">
                        <div className="card-body">
                          <div className="grid grid-cols-2 gap-2">
                            <div className="flex flex-col col-span-2">
                              <div className="text-sm">{l10n.label.accountHolderName}</div>
                              <div className="flex font-semibold">
                                {formValues.nameOfAccountHolder}
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <div className="text-sm">{l10n.label.bankName}</div>
                              <div className="flex font-semibold">{formValues.nameOfBank}</div>
                            </div>
                            <div className="flex flex-col">
                              <div className="text-sm">{l10n.label.bankPostalCode}</div>
                              <div className="flex font-semibold">{formValues.bankZIPCode}</div>
                            </div>
                            <div className="flex flex-col col-span-2">
                              <div className="text-sm">{l10n.label.iban}</div>
                              <div className="flex font-semibold">{formValues.IBANnumber}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          checked={finalApproval}
                          id="truthDeclaration"
                          name="truthDeclaration"
                          onChange={() => {
                            setFinalApproval(!finalApproval);
                            setIsValid(!finalApproval);
                          }}
                          required
                          type="checkbox"
                          title="The above information is truthful to the best of my knowledge."
                        />
                        <label className="form-check-label" htmlFor="truthDeclaration">
                          {l10n.label.aboveInformationIsTruthful}
                        </label>
                      </div>
                    </>
                  )
                );
            }
          })()}

          {step === SubmitClaimStep.Summary ? (
            <button
              className="btn btn-primary"
              disabled={!isValid || isSubmitting}
              onClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              {isSubmitting ? l10n.common.loading : l10n.btn.submit}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={(e) => {
                e.preventDefault();
                handleNext();
              }}
            >
              {l10n.common.next}
            </button>
          )}
        </form>
      </>
    </div>
  );
};

export default SubmitClaim;
